import { Controller, useWatch } from 'react-hook-form';
import {
  CreatorApprovedSwapsForm,
  exerciseNameKey,
} from '@client/new/features/exercise-swaps';
import { EditWorkoutVM } from '../view-models/workoutEdit.viewModel';

type SwapFieldPath =
  | `exercises.${number}`
  | `exercises.${number}.exercises.${number}`;

type NamedSingle = { path: string; name: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const namedSingle = (path: string, value: unknown): NamedSingle[] =>
  isRecord(value) && value.type === 2 && typeof value.name === 'string'
    ? [{ path, name: value.name }]
    : [];

// Singles in document order, top-level then circuit children, matching the
// walk creator-api uses to pick which same-named exercise owns the slot.
const namedSingles = (exercises: unknown): NamedSingle[] =>
  Array.isArray(exercises)
    ? exercises.flatMap((exercise, index) => {
        const path = `exercises.${index}`;
        const children =
          isRecord(exercise) && Array.isArray(exercise.exercises)
            ? exercise.exercises.flatMap((child, childIndex) =>
                namedSingle(`${path}.exercises.${childIndex}`, child),
              )
            : [];
        return [...namedSingle(path, exercise), ...children];
      })
    : [];

const sharesEarlierSlot = (
  exercises: unknown,
  fieldPath: SwapFieldPath,
  name: string,
): boolean => {
  const nameKey = exerciseNameKey(name);
  if (nameKey === '') return false;
  const owner = namedSingles(exercises).find(
    (single) => exerciseNameKey(single.name) === nameKey,
  );
  return owner !== undefined && owner.path !== fieldPath;
};

// Thin adapter wiring the self-contained exercise-swaps feature into the
// legacy RHF workout form. All swap behavior lives in the feature slice.
export const CreatorApprovedSwapsField = (p: {
  control: EditWorkoutVM['control'];
  influencerId: number;
  fieldPath: SwapFieldPath;
  disabled?: boolean;
}) => {
  const programId = useWatch({ control: p.control, name: 'programId' });
  const workoutSwapsEnabled = useWatch({
    control: p.control,
    name: 'swapsEnabled',
  });
  const exerciseName = useWatch({
    control: p.control,
    name: `${p.fieldPath}.name`,
  });
  const exerciseSets = useWatch({
    control: p.control,
    name: `${p.fieldPath}.sets`,
  });
  const exercises: unknown = useWatch({
    control: p.control,
    name: 'exercises',
  });
  if (!programId) return null;

  return (
    <Controller
      name={`${p.fieldPath}.swapConfig`}
      control={p.control}
      render={({ field }) => (
        <CreatorApprovedSwapsForm
          value={field.value}
          onChange={field.onChange}
          exerciseName={exerciseName ?? ''}
          exerciseSets={exerciseSets ?? []}
          influencerId={p.influencerId}
          programId={programId}
          workoutSwapsOff={workoutSwapsEnabled === false}
          unavailable={workoutSwapsEnabled === undefined}
          sharedWithEarlier={sharesEarlierSlot(
            exercises,
            p.fieldPath,
            exerciseName ?? '',
          )}
          disabled={p.disabled}
        />
      )}
    />
  );
};
